import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { Printer, FileDown } from 'lucide-react';
import { exportCSV } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

export default function MaintenanceReport() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState(`Q${Math.ceil((new Date().getMonth() + 1) / 3)}`);
  const company = useCompanyInfo();

  useEffect(() => {
    client.entities.MaintenanceRecord.list('-service_date', 500).then(r => { setRecords(r); setLoading(false); });
  }, []);

  const quarterStart = (q, y) => {
    const qNum = parseInt(q.replace('Q', '')) - 1;
    return new Date(y, qNum * 3, 1).toISOString().split('T')[0];
  };
  const quarterEnd = (q, y) => {
    const qNum = parseInt(q.replace('Q', ''));
    return new Date(y, qNum * 3, 0).toISOString().split('T')[0];
  };

  const qs = quarterStart(quarter, year);
  const qe = quarterEnd(quarter, year);

  const filtered = records.filter(r => r.service_date >= qs && r.service_date <= qe);
  const totalCost = filtered.reduce((s, r) => s + (r.cost || 0), 0);

  const csvRows = filtered.map(r => ({
    'Unit #': r.unit_number || '',
    'Service Type': r.service_type || '',
    Date: r.service_date || '',
    Vendor: r.vendor_name || '',
    Cost: (r.cost || 0).toFixed(2),
    'Next Due': r.next_service_date || '',
    Notes: r.notes || '',
  }));

  const handlePrint = () => {
    const cols = ['Unit #', 'Service Type', 'Date', 'Vendor', 'Cost', 'Next Due'];
    const dataRows = filtered.map(r => [
      { value: r.unit_number || '—', class: 'bold' }, r.service_type, r.service_date, r.vendor_name || '—',
      { value: `$${(r.cost || 0).toLocaleString()}`, class: 'negative' },
      r.next_service_date || '—',
    ]);
    const totalsRow = ['TOTAL MAINTENANCE COST', '', '', '', { value: `$${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, class: 'gold' }, ''];
    const cards = [
      { label: 'Service Records', value: filtered.length, color: '' },
      { label: 'Total Cost', value: `$${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: 'negative' },
      { label: 'Quarter', value: `${quarter} ${year}`, color: 'gold' },
    ];
    const html = generatePrintHTML('Quarterly Maintenance Report', `${quarter} ${year}`, cols, dataRows, totalsRow, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Quarter</label>
            <select value={quarter} onChange={e => setQuarter(e.target.value)}
              className="px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs">
              {['Q1', 'Q2', 'Q3', 'Q4'].map(q => <option key={q} value={q} className="bg-background">{q}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Year</label>
            <select value={year} onChange={e => setYear(Number(e.target.value))}
              className="px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs">
              {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y} className="bg-background">{y}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => exportCSV(csvRows, `maintenance-${quarter}-${year}.csv`)} disabled={filtered.length === 0}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/20 border border-primary/40 text-primary text-xs font-semibold hover:bg-primary/30 transition-colors disabled:opacity-40">
            Export CSV
          </button>
          <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
          <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
        </div>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Unit #', 'Service Type', 'Date', 'Vendor', 'Cost', 'Next Due'].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map(r => (
              <tr key={r.id} className="hover:bg-white/3">
                <td className="px-3 py-2.5 font-semibold text-primary">{r.unit_number || '—'}</td>
                <td className="px-3 py-2.5 text-foreground">{r.service_type}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r.service_date}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r.vendor_name || '—'}</td>
                <td className="px-3 py-2.5 font-semibold text-foreground">${(r.cost || 0).toLocaleString()}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r.next_service_date || '—'}</td>
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr className="border-t border-border bg-white/5 font-bold">
                <td colSpan={4} className="px-3 py-2.5 text-foreground">TOTAL MAINTENANCE COST</td>
                <td className="px-3 py-2.5 text-foreground">${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
        {filtered.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No maintenance records for {quarter} {year}.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
    </div>
  );
}